import { useEffect, useState } from 'react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { Select } from '../../components/ui/Select';
import { Toggle } from '../../components/ui/Toggle';
import { Toast } from '../../components/ui/Toast';
import { AutoSaveStatus } from '../../components/ui/AutoSaveStatus';
import { useAutoSave } from '../../lib/useAutoSave';
import { APP_LANGUAGES, APP_LANGUAGE_LABEL, THEME_MODES, THEME_MODE_LABEL, type AppLanguage, type ThemeMode } from '../../types';

export function AppearanceScreen() {
  const { themeMode, language, profile, threeColumnLayout, setThemeMode, setLanguage, setThreeColumnLayout, syncSettings } =
    useStore();
  const [mode, setMode] = useState<ThemeMode>(themeMode);
  const [lang, setLang] = useState<AppLanguage>(language);
  const [error, setError] = useState('');

  const auto = useAutoSave({ mode, lang }, async ({ mode, lang }) => {
    setThemeMode(mode);
    setLanguage(lang);
    await syncSettings(profile, mode, lang);
  });

  useEffect(() => {
    if (auto.error) setError('Saved locally, but could not sync to the server.');
  }, [auto.error]);

  return (
    <div className="flex flex-col gap-5" onBlur={auto.flush}>
      <ScreenHeader title="Appearance" action={<AutoSaveStatus status={auto.status} onRetry={auto.retry} />} />

      <FormSection title="Theme">
        <Select label="Theme" value={mode} onChange={(e) => setMode(e.target.value as ThemeMode)}>
          {THEME_MODES.map((m) => (
            <option key={m} value={m}>
              {THEME_MODE_LABEL[m]}
            </option>
          ))}
        </Select>
        <Select label="Language" value={lang} onChange={(e) => setLang(e.target.value as AppLanguage)}>
          {APP_LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {APP_LANGUAGE_LABEL[l]}
            </option>
          ))}
        </Select>
      </FormSection>

      {/* Local-only, unlike Theme/Language above - applies and persists to
          localStorage immediately on toggle rather than waiting for the
          Save button (which also syncs Theme/Language to the server via
          syncSettings - this preference is device-specific and was never
          meant to round-trip through that). Only visible effect is on
          ChatScreen's Messages/Charts/Reports layout once the viewport is
          wide enough (xl breakpoint, 1280px+) to fit three columns at all
          - narrower screens show the same single-pane tab view either
          way, so this has no effect there. */}
      <FormSection title="Layout">
        <Toggle
          label="Split Messages / Charts / Reports into three columns on wide screens"
          checked={threeColumnLayout}
          onChange={setThreeColumnLayout}
        />
      </FormSection>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
    </div>
  );
}
